import Dexie from "dexie";
import moment from "moment";
import { notify, nameFormatting, manageErrorLogs } from "./commonFunction";
import { reqToStartSyncProcess } from "../store/slice/offlineSlice";

export const DB = new Dexie("fogapp");
DB.version(1)
  .stores({
    userData: "++id",
    errorLogs: "++id",
    mediaFiles: "++id, file",
    systemLogs: "++id",
    activeCycle: "++id",
    lastAPICallTime: "++id",
    takedDetails: "++id",
    addedTakeDetails: "++id",
  })
  .upgrade(async (tx) => {});

DB.on("close", async () => {
  try {
    await reconnectDB();
  } catch (error) {
    manageErrorLogs(error);
  }
});

DB.on("blocked", async () => {
  try {
    await reconnectDB();
  } catch (error) {
    manageErrorLogs(error);
  }
});

const reconnectDB = async (retries = 3) => {
  let lastError;
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      if (!DB.isOpen()) {
        await DB.open();
      }
      return true;
    } catch (error) {
      lastError = error;
      if (attempt < retries) {
        await new Promise((resolve) => setTimeout(resolve, 500 * attempt));
      } else {
        manageErrorLogs(error);
        throw new Error(lastError?.message || error);
      }
    }
  }
};

export const safeTransaction = async (tableNames, action, retries = 3) => {
  let lastError;
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      await reconnectDB();
      const result = await DB.transaction("rw", tableNames, async (tx) => {
        try {
          return await action(tx);
        } catch (innerError) {
          throw innerError;
        }
      });
      return result;
    } catch (error) {
      lastError = error;
      if (attempt < retries) {
        await new Promise((resolve) => setTimeout(resolve, 200 * attempt));
      } else {
        manageErrorLogs(error || lastError);
        throw error || lastError;
      }
    }
  }
};

export const isTableExists = (table) => {
  if (!DB.tables.some((t) => t.name === table)) {
    throw new Error(`Table '${table}' does not exist.`);
  }
};

export const isFieldIndexed = (table, field) => {
  const tableSchema = DB.tables.find((t) => t.name === table).schema;
  return (
    tableSchema.indexes.some((index) => index.name === field) ||
    tableSchema.primKey.name === field
  );
};

export const checkTablesInDB = (tables) => {
  try {
    const tableArr = Array.isArray(tables) ? tables : [tables];
    tableArr.forEach(isTableExists);
  } catch (error) {
    throw error;
  }
};

export const clearTables = async (tx, tables) => {
  try {
    const tableArr = Array.isArray(tables) ? tables : [tables];
    for (const name of tableArr) {
      await tx.table(name).clear();
    }
  } catch (error) {
    throw error;
  }
};

export const clearTablesWithTran = async (tables) => {
  try {
    const tableArr = Array.isArray(tables) ? tables : [tables];
    await safeTransaction(tableArr, async (tx) => {
      for (const name of tableArr) {
        await tx.table(name).clear();
      }
    });
  } catch (error) {
    manageErrorLogs(error);
    return null;
  }
};

export const getAllEntriesFromDB = async (table) => {
  try {
    if (!table) {
      throw new Error(`Invalid or missing data!`);
    }
    isTableExists(table);

    let entries = [];
    await safeTransaction([table], async () => {
      entries = await DB.table(table).toArray();
    });
    return entries;
  } catch (error) {
    manageErrorLogs(error);
    return [];
  }
};

export const getMultipleEntriesFromDB = async (tables) => {
  try {
    if (!Array.isArray(tables) || tables.length === 0) {
      throw new Error(`Invalid or missing table names!`);
    }
    tables.forEach(isTableExists);
    let results = {};
    await safeTransaction(tables, async (tx) => {
      for (const table of tables) {
        const data = await tx.table(table).toArray();
        if (!data) {
          throw new Error(`No data found in table: ${table}`);
        }
        results[table] = data;
      }
    });
    return results;
  } catch (error) {
    throw error;
  }
};

export const updateMultipleEntriesInDB = async (
  tables,
  field,
  value,
  newData
) => {
  try {
    if (
      !tables ||
      !Array.isArray(tables) ||
      tables.length === 0 ||
      !field ||
      !value ||
      !newData
    ) {
      throw new Error(`Invalid or missing data!`);
    }
    tables.forEach((table) => isTableExists(table));
    let updatedIDs = {};
    await safeTransaction(tables, async (tx) => {
      for (const table of tables) {
        const tableRef = tx.table(table);
        const existingEntry = await tableRef
          .where(field)
          .equals(value)
          .toArray();

        if (existingEntry.length > 0) {
          await tableRef.update(existingEntry[0]?.id, newData);
          updatedIDs[table] = existingEntry[0]?.id;
        } else {
          const newID = await tableRef.put(newData);
          updatedIDs[table] = newID;
        }
      }
    });
    return updatedIDs;
  } catch (error) {
    throw error;
  }
};

export const getEntryFromDB = async (table, id) => {
  try {
    if (!table || !id) {
      throw new Error(`Invalid or missing data!`);
    }
    isTableExists(table);

    let entry = null;
    await safeTransaction([table], async () => {
      entry = await DB.table(table).get(id);
    });
    return entry;
  } catch (error) {
    manageErrorLogs(error);
    return null;
  }
};

export const getEntryByFieldFromDB = async (table, fields, values) => {
  try {
    if (!table || !fields || !values) {
      throw new Error(`Invalid or missing data!`);
    }
    isTableExists(table);

    let entries = [];
    if (typeof fields === "string" && typeof values !== "object") {
      if (!isFieldIndexed(table, fields)) {
        throw new Error(`Field '${fields}' in '${table}' is not indexed.`);
      }
      await safeTransaction(table, async () => {
        entries = await DB.table(table).where(fields).equals(values).toArray();
      });
    } else if (Array.isArray(fields) && Array.isArray(values)) {
      if (fields.length !== values.length) {
        throw new Error(`Fields and values arrays must have the same length`);
      }
      for (const field of fields) {
        if (!isFieldIndexed(table, field)) {
          throw new Error(`Field '${field}' in '${table}' is not indexed.`);
        }
      }
      await safeTransaction(table, async () => {
        let query = DB.table(table);
        for (let i = 0; i < fields.length; i++) {
          query = query.and((entry) => entry[fields[i]] === values[i]);
        }
        entries = await query.toArray();
      });
    } else {
      throw new Error(`Invalid data types for fields and values!`);
    }
    return entries;
  } catch (error) {
    manageErrorLogs(error);
    return [];
  }
};

export const insertEntryInDB = async (table, newData) => {
  try {
    if (!table || !newData) {
      throw new Error(`Invalid or missing data!`);
    }
    isTableExists(table);

    let id = "";
    await safeTransaction([table], async () => {
      id = await DB.table(table).add(newData);
    });
    return id;
  } catch (error) {
    manageErrorLogs(error);
    return null;
  }
};

export const insertEntriesInDB = async (table, newData) => {
  try {
    if (!table || !newData || !Array.isArray(newData)) {
      throw new Error(`Invalid or missing data!`);
    }
    isTableExists(table);

    let result = "";
    await safeTransaction([table], async () => {
      result = await DB.table(table).bulkPut(newData);
    });
    return result;
  } catch (error) {
    manageErrorLogs(error);
    return null;
  }
};

export const updateEntryInDB = async (table, field, value, newData) => {
  try {
    if (!table || !field || !value || !newData) {
      throw new Error(`Invalid or missing data!`);
    }
    isTableExists(table);
    if (!isFieldIndexed(table, field)) {
      throw new Error(`Field '${field}' in '${table}' is not indexed.`);
    }

    let id = "";
    await safeTransaction([table], async () => {
      const isExist = await getEntryByFieldFromDB(table, field, value);
      if (isExist?.length > 0) {
        await DB.table(table).update(isExist[0]?.id, newData);
        id = isExist[0]?.id;
      } else {
        id = await DB.table(table).add(newData);
      }
    });
    return id;
  } catch (error) {
    manageErrorLogs(error);
    return false;
  }
};

export const reqToImportTableData = async (file, dispatch) => {
  try {
    dispatch(reqToStartSyncProcess(true));
    const reader = new FileReader();
    reader.onload = async (event) => {
      const importData = JSON.parse(event.target.result);
      await DB.transaction(
        "rw",
        Object.keys(importData).map((name) => DB.table(name)),
        async () => {
          for (const [tableName, records] of Object.entries(importData)) {
            const table = DB.table(tableName);
            if (table) {
              await table.clear();
              await table.bulkAdd(records);
            } else {
              notify(
                `Table "${tableName}" does not exist in the database.`,
                "warning"
              );
            }
          }
        }
      );
      dispatch(reqToStartSyncProcess(false));
      notify("Database imported successfully!", "success");
    };
    reader.onerror = () => {
      dispatch(reqToStartSyncProcess(false));
      notify("Failed to read the file!", "error");
    };
    reader.readAsText(file);
  } catch (error) {
    manageErrorLogs(error);
    dispatch(reqToStartSyncProcess(false));
    notify("Failed to import database!", "error");
  }
};

export const reqToExportTableData = async (tables, dispatch) => {
  try {
    dispatch(reqToStartSyncProcess(true));
    const userData = await getAllEntriesFromDB("userData");
    const newUserData = userData?.length > 0 ? userData[0] : "";
    const tablesToExport = tables.map((name) => DB.table(name));
    const data = await DB.transaction("r", tablesToExport, async () => {
      const exportData = {};
      for (const table of tablesToExport) {
        exportData[table.name] = await table.toArray();
      }
      return exportData;
    });

    const blob = new Blob([JSON.stringify(data)], {
      type: "application/json",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${
      newUserData
        ? `${nameFormatting(newUserData)}_${moment().format("YYYY-MM-DD")}_`
        : `${moment().format("YYYY-MM-DD")}_`
    }database.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    notify("Database exported successfully!", "success");
    setTimeout(() => {
      dispatch(reqToStartSyncProcess(false));
    }, 500);
  } catch (error) {
    manageErrorLogs(error);
    dispatch(reqToStartSyncProcess(false));
    notify("Failed to exporting database!", "error");
  }
};
